/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 08/09/22
 */

import {
    TouchedState,
    useTouchedSelector,
    withTouchedContext,
} from '@/components/WorkplanItemForm/TouchedStateProvider';
import { chakraCells, chakraRenderers } from '@/forms-renderers/chakra-ui';
import InlineRefParser from '@/forms-renderers/inlineRefParser/inlineRefParser';
import { namedMemo } from '@/utils/namedMemo';
import { createAjv, JsonFormsCore, JsonSchema, UISchemaElement } from '@jsonforms/core';
import { JsonForms } from '@jsonforms/react';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { WorkplanItemFormConfig } from './WorkplanItemForm';

export type HandleChangeFunc = (state: Pick<JsonFormsCore, 'data' | 'errors'>) => void;
export type HandleTouchedChangeFunc = (state: { touched: TouchedState }) => void;

interface BaseJsonFormProps {
    initialData: Record<string, unknown>;
    schema: JsonSchema;
    uischema: UISchemaElement;
    onChange?: HandleChangeFunc;
    onTouchedChange?: HandleTouchedChangeFunc;
    config: WorkplanItemFormConfig;
}

const BaseJsonForm = namedMemo(
    'FormStuff',
    ({ initialData, uischema, schema, onChange, onTouchedChange, config }: BaseJsonFormProps) => {
        const ajv = useMemo(
            () =>
                createAjv({
                    useDefaults: true,
                    allErrors: true,
                    $data: true,
                }),
            [],
        );

        const compiledSchema = useMemo(() => InlineRefParser.expandRefs(schema), [schema]);
        const compiledUISchema = useMemo(() => InlineRefParser.expandRefs(uischema), [uischema]);

        const staticProps = useMemo(
            () => ({
                uischema: compiledUISchema,
                schema: compiledSchema,
                ajv,
                renderers: chakraRenderers,
                cells: chakraCells,
            }),
            [compiledUISchema, compiledSchema],
        );

        const initialDataRef = useRef(initialData);

        const fullTouchedState = useTouchedSelector((state) => state);
        useEffect(() => {
            if (onTouchedChange) {
                onTouchedChange({ touched: fullTouchedState });
            }
        }, [fullTouchedState, onTouchedChange]);

        return <JsonForms {...staticProps} onChange={onChange} data={initialDataRef.current} config={config} />;
    },
    isEqual,
);

export default withTouchedContext(BaseJsonForm);
